import { computed } from "vue";
import { useRouter, useRoute } from "vue-router/composables";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";

dayjs.extend(weekday);

const ALL_OPTION = {
    name: "All",
    id: "all",
    display: "All",
};

const compareIds = (a, b) => {
    if (a === "all" || b === "all") {
        return a === b;
    }
    return a?.toString() === b?.toString();
}

const findElement = (array, id) => {
    if (!id || id === "all") {
        return ALL_OPTION;
    }
    const element = array.find(item => compareIds(item.id, id));
    return element || ALL_OPTION;
}

const resolveArraySource = (source) => {
    const value = typeof source === "function"
        ? source()
        : source?.value !== undefined
            ? source.value
            : source;
    return Array.isArray(value) ? value : [];
};

export function useUrlFilters(locationsOptions = [], experiencesOptions = []) {
    const router = useRouter();
    const route = useRoute();

    const getLocations = () => resolveArraySource(locationsOptions);
    const getExperiences = () => resolveArraySource(experiencesOptions);

    // Helper function to get default date range (current week)
    const getDefaultDateRange = () => {
        const today = dayjs();
        let startDate = today.weekday(1);
        let endDate = today.weekday(7);
        if (today.weekday() === 0) {
            startDate = today.weekday(-6);
            endDate = today.weekday(0);
        }
        return [startDate, endDate];
    };

    // Helper function to get all available xcubes from locations
    const getAllXCubes = () => {
        return getLocations().reduce((acc, location) => {
            const locationXCubes = location.xcubes?.map(xcube => ({
                ...xcube,
                location: location.id,
                display: `${location.name} - ${xcube.name} ${xcube.letter}`
            })) || [];
            return acc.concat(locationXCubes);
        }, []);
    };

    // Helper function to update URL query parameters
    const updateQuery = (updates) => {
        const newQuery = { ...route.query, ...updates };
        router.push({ query: newQuery }).catch(() => {
            // Ignore NavigationDuplicated errors
        });
    };

    // Computed properties for each filter
    const range = computed({
        get() {
            const startDate = route.query.startDate;
            const endDate = route.query.endDate;
            
            if (startDate && endDate) {
                return [dayjs(startDate), dayjs(endDate)];
            }
            
            return getDefaultDateRange();
        },
        set([startDate, endDate]) {
            updateQuery({
                startDate: startDate.format("YYYY-MM-DD"),
                endDate: endDate.format("YYYY-MM-DD")
            });
        }
    });

    const selectedLocation = computed({
        get() {
            const locationId = route.query.locationId;
            return findElement(getLocations(), locationId);
        },
        set(location) {
            const value = location?.id === "all" ? undefined : location?.id;
            updateQuery({ locationId: value });
        }
    });

    const selectedXCube = computed({
        get() {
            const xcubeId = route.query.xcubeId;
            return findElement(getAllXCubes(), xcubeId);
        },
        set(xcube) {
            const value = xcube?.id === "all" ? undefined : xcube?.id.toString();
            updateQuery({ xcubeId: value });
        }
    });

    const selectedExperience = computed({
        get() {
            const experienceId = route.query.experienceId;
            return findElement(getExperiences(), experienceId);
        },
        set(experience) {
            const value = experience?.id === "all" ? undefined : experience?.id;
            updateQuery({ experienceId: value });
        }
    });

    const bookingOptions = computed({
        get() {
            const options = route.query.options;
            if (options) {
                return options.split(',').filter(Boolean);
            }
            return [];
        },
        set(options) {
            const value = options?.length > 0 ? options.join(',') : undefined;
            updateQuery({ options: value });
        }
    });

    const limit = computed({
        get() {
            const limitValue = route.query.limit;
            return limitValue ? Number.parseInt(limitValue, 10) : 1000;
        },
        set(limitValue) {
            const value = limitValue !== 1000 ? limitValue.toString() : undefined;
            updateQuery({ limit: value });
        }
    });

    const past = computed({
        get() {
            return route.query.past === "true";
        },
        set(pastValue) {
            const value = pastValue ? "true" : undefined;
            updateQuery({ past: value });
        }
    });

    // Computed filters object that matches the old store's format
    const filters = computed(() => {
        const [startDate, endDate] = range.value;
        return {
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: endDate.format("YYYY-MM-DD"),
            locationId: selectedLocation.value.id,
            xCubeId: selectedXCube.value.id,
            experienceId: selectedExperience.value.id,
            options: [...bookingOptions.value],
            limit: limit.value,
            past: past.value,
        };
    });

    return {
        // Computed properties
        range,
        selectedLocation,
        selectedXCube,
        selectedExperience,
        bookingOptions,
        limit,
        past,
        filters,
    };
}